import { FC, useEffect, useMemo } from 'react';
import { FeaturesetValues } from '../../network';
import { MetricKeyEnum, useLayerStore } from '../LayerControls/layerStore';
import { jointLocationDict } from '../../types/sagittalTypes';
import { ThreeDPoint } from '../3dModels/ThreeDPoint';
import { Line3D } from '../3dModels/JointRenderer';
import { ThreeDLabel } from '../3dModels/ThreeDLabel';
import { useHighlighter } from '../ui/useHighlighter';

interface AnkleFlexionProps {
  featuresetValues: FeaturesetValues | undefined;
}

const layerKey = MetricKeyEnum.AnkleFlexion;

export const AnkleFlexion: FC<AnkleFlexionProps> = ({ featuresetValues }) => {
  const { addLayerState, isLayerVisible } = useLayerStore();
  useHighlighter({ layerKey });

  const result = useMemo(() => {
    try {
      const sagittalDisplayDict = featuresetValues?.sagittal_display_dict;
      const jointLocations = sagittalDisplayDict?.joint_locations;
      const legAngles = sagittalDisplayDict?.leg_angles;

      if (!legAngles) {
        throw new Error('Leg angles are not defined for ankle flexion');
      }
      if (!jointLocations) {
        throw new Error('Joint locations are not defined for ankle flexion');
      }

      const ankleFlexionLeft = legAngles.ankle_flexion_left;
      const ankleFlexionRight = legAngles.ankle_flexion_right;
      const ankleFlexionAvg = legAngles.ankle_flexion_angle;

      const leftAnkle = jointLocations[jointLocationDict.left_ankle];
      const rightAnkle = jointLocations[jointLocationDict.right_ankle];

      const leftKnee = jointLocations[jointLocationDict.left_knee];
      const rightKnee = jointLocations[jointLocationDict.right_knee];

      return {
        ankleFlexionLeft,
        ankleFlexionRight,
        ankleFlexionAvg,
        leftAnkle,
        rightAnkle,
        leftKnee,
        rightKnee,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [featuresetValues]);

  useEffect(() => {
    if (!result) {
      return;
    }
    addLayerState(layerKey, {
      key: layerKey,
      visible: false,
    });
  }, [addLayerState, result]);

  if (!result) {
    return null;
  }

  const {
    ankleFlexionLeft,
    ankleFlexionRight,
    leftAnkle,
    rightAnkle,
    leftKnee,
    rightKnee,
    // ankleFlexionAvg,
  } = result;

  const showLayer = isLayerVisible(layerKey);

  return (
    <group name={layerKey} visible={showLayer}>
      {/* ankle */}
      <ThreeDPoint position={leftAnkle} alwaysOnTop />
      <ThreeDPoint position={rightAnkle} alwaysOnTop />

      {/* knee */}
      <ThreeDPoint position={leftKnee} alwaysOnTop />
      <ThreeDPoint position={rightKnee} alwaysOnTop />

      {/* ankle flexion */}
      <Line3D start={leftAnkle} end={leftKnee} />
      <Line3D start={rightAnkle} end={rightKnee} />

      <ThreeDLabel
        plane='sagittal'
        position={leftAnkle}
        label={`L ${ankleFlexionLeft.toFixed(2)}°`}
        visible={showLayer}
        divPosition={{
          right: 30,
        }}
        layerKey={layerKey}
      />

      <ThreeDLabel
        plane='sagittal'
        position={rightAnkle}
        label={`R ${ankleFlexionRight.toFixed(2)}°`}
        visible={showLayer}
        divPosition={{
          left: 30,
        }}
        layerKey={layerKey}
      />
    </group>
  );
};
